import { Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import {
  ArrowUpRight,
  CheckCircle2,
  Circle,
  CircleX,
  Clock,
  RotateCcw,
  Sparkles,
  Target,
  Zap,
} from 'lucide-react';
import type { CampaignStep } from '@seo/types';
import { toast } from 'sonner';
import { PageHeader } from '@/components/page-header';
import { GlassSurface, GlowCard } from '@/components/glow-card';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { Button } from '@/components/ui/button';
import { DetailError } from '@/components/detail-page';
import { fadeUp, stagger } from '@/components/motion';
import { useCampaign } from '@/features/campaigns/use-campaign';
import { useCampaignSteps } from '@/features/campaigns/use-campaign-steps';
import { useCampaignEvents } from '@/features/campaigns/use-campaign-events';
import { useApproveCampaign } from '@/features/campaigns/use-approve-campaign';
import { useRetryCampaignStep } from '@/features/campaigns/use-retry-campaign-step';
import { summarizeApiError } from '@/lib/api';
import { cn } from '@/lib/cn';

const STATUS_ICON: Record<string, JSX.Element> = {
  pending: <Circle className="h-4 w-4 text-muted-foreground" />,
  queued: <Clock className="h-4 w-4 text-amber-500" />,
  running: <Zap className="h-4 w-4 text-primary" />,
  completed: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  failed: <CircleX className="h-4 w-4 text-destructive" />,
  skipped: <Circle className="h-4 w-4 text-muted-foreground" />,
  cancelled: <CircleX className="h-4 w-4 text-muted-foreground" />,
};

export function CampaignDetailRoute(): JSX.Element {
  const { campaignId } = useParams({ strict: false }) as { campaignId: string };
  const campaign = useCampaign(campaignId);
  const steps = useCampaignSteps(campaignId);
  const events = useCampaignEvents(campaignId);
  const approve = useApproveCampaign(campaignId);
  const retryStep = useRetryCampaignStep(campaignId);

  const c = campaign.data;
  const parsed = c?.parsed_goal as
    | { metric?: string; target_delta_pct?: number; target_scope?: string; time_horizon_days?: number }
    | undefined;
  const strategy = c?.strategy as { archetype?: string; rationale?: string } | undefined;

  // Terminal error — render it instead of the never-resolving spinner.
  if (campaign.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={campaign.error}
          entityKind="campaign"
          backTo="/campaigns"
          backLabel="Campaigns"
        />
      </div>
    );
  }

  return (
    <motion.div className="space-y-6" initial="hidden" animate="show" variants={stagger}>
      <PageHeader
        title="Campaign"
        description={c?.goal ?? '...'}
        actions={c ? <StatusPill status={c.status} /> : null}
      />

      {!c ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <>
          <motion.section variants={fadeUp} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <GlassSurface className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Parsed metric
              </div>
              <div className="mt-2 text-lg font-semibold capitalize">
                {parsed?.metric?.replace('_', ' ') ?? '—'}
              </div>
              <div className="text-xs text-muted-foreground">
                {parsed?.target_delta_pct != null ? `+${parsed.target_delta_pct}% ` : ''}
                {parsed?.target_scope ? `on “${parsed.target_scope}”` : ''}
              </div>
            </GlassSurface>
            <GlassSurface className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Strategy
              </div>
              <div className="mt-2 text-lg font-semibold capitalize">
                {strategy?.archetype?.replace('_', ' ') ?? '—'}
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2">
                {strategy?.rationale ?? ''}
              </div>
            </GlassSurface>
            <GlassSurface className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Horizon
              </div>
              <div className="mt-2 text-lg font-semibold">{c.horizon_days ?? '—'} days</div>
              <div className="text-xs text-muted-foreground">
                {parsed?.time_horizon_days ? `parsed: ${parsed.time_horizon_days}d` : ''}
              </div>
            </GlassSurface>
          </motion.section>

          {c.status === 'awaiting_approval' ? (
            <motion.section variants={fadeUp}>
              <GlowCard className="flex items-center justify-between p-4">
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    <Target className="h-4 w-4" /> Plan ready for approval
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Approve to start executing the steps below.
                  </div>
                </div>
                <Button
                  onClick={() =>
                    approve
                      .mutateAsync({})
                      .then(() => toast.success('Campaign approved'))
                      .catch((e) => {
                        const s = summarizeApiError(e, { fallbackTitle: 'Approval failed' });
                        toast.error(s.title, { description: s.description });
                      })
                  }
                  disabled={approve.isPending}
                >
                  {approve.isPending ? <Spinner className="mr-2" /> : null}
                  Approve plan
                </Button>
              </GlowCard>
            </motion.section>
          ) : null}

          {c.briefing_md ? (
            <motion.section variants={fadeUp}>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4" /> Executive briefing
              </div>
              <GlassSurface className="p-4">
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
                  {c.briefing_md}
                </pre>
              </GlassSurface>
            </motion.section>
          ) : null}

          <motion.section variants={fadeUp}>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Zap className="h-4 w-4" /> Steps
              {steps.data ? (
                <span className="text-xs font-normal text-muted-foreground">
                  ({steps.data.items.length})
                </span>
              ) : null}
            </div>
            {steps.isLoading ? (
              <Spinner />
            ) : (
              <GlassSurface className="p-2">
                <ol className="space-y-1">
                  {(steps.data?.items ?? []).map((s) => {
                    const hasArtifact = s.child_run_id != null;
                    const rowClass = cn(
                      'group flex items-start gap-3 rounded-md border border-border/60 bg-background/40 p-3',
                      s.status === 'running' && 'border-primary/40 bg-primary/5',
                      hasArtifact && 'transition hover:border-primary/40 hover:bg-primary/5',
                    );
                    return (
                      <li key={s.id}>
                        <StepArtifactWrapper step={s} className={rowClass}>
                          <div className="mt-0.5">
                            {STATUS_ICON[s.status] ?? STATUS_ICON.pending}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-muted-foreground">
                                #{s.seq}
                              </span>
                              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                                {s.step_type.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="mt-1 text-sm font-medium text-foreground">
                              {s.title}
                            </div>
                            {s.rationale ? (
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {s.rationale}
                              </div>
                            ) : null}
                            {s.failure_reason ? (
                              <div className="mt-1 text-xs text-destructive">
                                {s.failure_reason}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            {s.status === 'failed' ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  retryStep
                                    .mutateAsync(s.id)
                                    .then(() => toast.success('Retry started'))
                                    .catch((err) => {
                                      const summary = summarizeApiError(err, {
                                        fallbackTitle: 'Retry failed',
                                      });
                                      toast.error(summary.title, {
                                        description: summary.description,
                                      });
                                    });
                                }}
                                disabled={
                                  retryStep.isPending && retryStep.variables === s.id
                                }
                                title="Retry this step"
                                aria-label="Retry step"
                                className={cn(
                                  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 bg-background/60 text-muted-foreground transition',
                                  'hover:border-primary/60 hover:bg-primary/10 hover:text-primary',
                                  'disabled:cursor-not-allowed disabled:opacity-50',
                                )}
                              >
                                {retryStep.isPending && retryStep.variables === s.id ? (
                                  <Spinner className="h-3.5 w-3.5" />
                                ) : (
                                  <RotateCcw className="h-3.5 w-3.5" />
                                )}
                              </button>
                            ) : null}
                            <StatusPill status={s.status} />
                            {hasArtifact ? (
                              <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
                            ) : null}
                          </div>
                        </StepArtifactWrapper>
                      </li>
                    );
                  })}
                </ol>
              </GlassSurface>
            )}
          </motion.section>

          <motion.section variants={fadeUp}>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Clock className="h-4 w-4" /> Timeline
            </div>
            <GlassSurface className="p-2">
              <ol className="space-y-1">
                {(events.data?.items ?? []).map((e) => (
                  <li
                    key={e.id}
                    className="flex items-start gap-3 rounded-md border border-border/60 bg-background/40 p-2"
                  >
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {new Date(e.created_at).toLocaleTimeString()}
                    </span>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {e.kind}
                    </span>
                    <span className="ml-auto text-xs text-foreground/80">
                      {e.detail ?? ''}
                    </span>
                  </li>
                ))}
              </ol>
            </GlassSurface>
          </motion.section>
        </>
      )}
    </motion.div>
  );
}

/**
 * Wraps a campaign step row's contents in a router `<Link>` to the artifact
 * the step produced (audit / brief / calendar / …). Falls back to a plain
 * `<div>` when the step has no `child_run_id` (still pending, skipped, or
 * `a4_report` which the orchestrator auto-skips).
 */
function StepArtifactWrapper({
  step,
  className,
  children,
}: {
  step: CampaignStep;
  className: string;
  children: ReactNode;
}): JSX.Element {
  const id = step.child_run_id;
  if (!id) return <div className={className}>{children}</div>;
  switch (step.step_type) {
    case 'a1_audit':
      return (
        <Link to="/audits/$auditId" params={{ auditId: id }} className={className}>
          {children}
        </Link>
      );
    case 'a2_keyword':
      return (
        <Link
          to="/keywords/$keywordRunId"
          params={{ keywordRunId: id }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'a3_brief':
      return (
        <Link to="/briefs/$briefId" params={{ briefId: id }} className={className}>
          {children}
        </Link>
      );
    case 'a5_competitor':
      return (
        <Link to="/competitors/$runId" params={{ runId: id }} className={className}>
          {children}
        </Link>
      );
    case 'a5_analysis':
      return (
        <Link
          to="/competitor-analyses/$analysisId"
          params={{ analysisId: id }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'a6_calendar':
      return (
        <Link
          to="/calendars/$calendarId"
          params={{ calendarId: id }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'a4_report':
    default:
      return <div className={className}>{children}</div>;
  }
}
